import { useInView, useMotionValue, useSpring, motion } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'

interface CounterProps {
  value: string
  className?: string
  duration?: number
}

export function Counter({ value, className, duration = 2 }: CounterProps) {
  const ref = useRef<HTMLSpanElement>(null)
  const isInView = useInView(ref, { once: true, margin: '-50px' })
  const [displayValue, setDisplayValue] = useState(value)

  // Extract numeric value and suffix
  const match = value.match(/^([\d.]+)(.*)$/)
  const numericValue = match ? parseFloat(match[1]) : 0
  const suffix = match ? match[2] : value

  const motionValue = useMotionValue(0)
  const springValue = useSpring(motionValue, {
    damping: 30,
    stiffness: 100,
    duration: duration * 1000,
  })

  useEffect(() => {
    if (isInView && numericValue > 0) {
      motionValue.set(numericValue)
    }
  }, [isInView, numericValue, motionValue])

  useEffect(() => {
    const unsubscribe = springValue.on('change', (latest) => {
      if (numericValue > 0) {
        const formatted = numericValue % 1 === 0
          ? Math.floor(latest).toString()
          : latest.toFixed(1)
        setDisplayValue(formatted + suffix)
      }
    })
    return unsubscribe
  }, [springValue, suffix, numericValue])

  // For non-numeric values, just fade in
  if (!match || numericValue === 0) {
    return (
      <motion.span
        ref={ref}
        initial={{ opacity: 0 }}
        animate={isInView ? { opacity: 1 } : { opacity: 0 }}
        transition={{ duration: 0.5 }}
        className={className}
      >
        {value}
      </motion.span>
    )
  }

  return (
    <span ref={ref} className={className}>
      {displayValue}
    </span>
  )
}
